// Leitura, Cursos e Mídia › Relatório (texto 16, Parte A) e Meta anual (Parte B).
// Mesmo visual do Relatório de estudos (◀ período ▶, total no topo, gráfico, listas e histórico de relatórios)
// com as mesmas peças (components/relatorio/Periodo.tsx). A regra de qual item entra no período está em
// lib/leituraRelatorio.ts. O PDF não leva anotações pessoais (resumo, comentários, anotações dos livros).
import { useState } from 'react';
import { Alert, Pressable, ScrollView, Text, View } from 'react-native';
import type { Reg } from '../store/types';
import { setDoc, useDoc } from '../store/store';
import { MES_CURTO, addDays, brYmd, nomeMes, parseYmd, today, ymd } from '../lib/date';
import { N_, t } from '../lib/i18n';
import { gerarECompartilharPdf, type BlocoPdf } from '../lib/pdf';
import { marcarAbaAtual } from '../lib/relatorios';
import { listaDeReferencias, type Estilo } from '../lib/referencia';
import { concluidosNoAno, itensDoPeriodo, melhorSequencia, porDia, ritmoMeta, tipoDe, type Linha, type TipoLeitura } from '../lib/leituraRelatorio';
import { Btn, Chip, Field, Sheet } from './ui';
import { PickerModal } from './Pickers';
import { RelatoriosGerados } from './RelatoriosGerados';
import { Barras, CartaoTotal, NavPeriodo } from './relatorio/Periodo';
import { C, alpha } from './theme';

type Modo = 'semana' | 'mes' | 'ano' | 'datas';
const DIAS = [N_('Seg'), N_('Ter'), N_('Qua'), N_('Qui'), N_('Sex'), N_('Sáb'), N_('Dom')];
const num = (v: unknown) => { const n = typeof v === 'number' ? v : parseFloat(String(v ?? '').replace(',', '.')); return isFinite(n) ? n : 0; };
const segunda = (s: string) => { const d = parseYmd(s); const w = (d.getDay() + 6) % 7; return addDays(s, -w); };
const inicioMes = (s: string) => s.slice(0, 8) + '01';
const fimMes = (s: string) => { const d = parseYmd(inicioMes(s)); return ymd(new Date(d.getFullYear(), d.getMonth() + 1, 0)); };
const somaMes = (s: string, n: number) => { const d = parseYmd(inicioMes(s)); return ymd(new Date(d.getFullYear(), d.getMonth() + n, 1)); };
const inicioDe = (m: Modo, s: string) => (m === 'semana' ? segunda(s) : m === 'mes' ? inicioMes(s) : m === 'ano' ? s.slice(0, 4) + '-01-01' : s);
const fimDe = (m: Modo, ini: string, fimEscolhido: string) => (m === 'semana' ? addDays(ini, 6) : m === 'mes' ? fimMes(ini) : m === 'ano' ? ini.slice(0, 4) + '-12-31' : fimEscolhido);
const estrelas = (n: number) => (n ? '★'.repeat(n) : '');

const NOME_TIPO: Record<TipoLeitura, string> = { livro: N_('Livros'), midia: N_('Filmes e séries'), curso: N_('Cursos'), artigo: N_('Artigos e sites') };
// texto 21 C8: as quatro partes no relatório (a meta anual continua só para livros, filmes e cursos)
const TODOS_TIPOS: TipoLeitura[] = ['livro', 'artigo', 'midia', 'curso'];

/** Texto 16 B: "12 de 30 livros em 2026", com a barra e o ritmo que falta. Opcional; muda ou apaga a qualquer momento. */
export function MetaAnual({ tipo, cor, ano: anoP, discreta = true }: { tipo: Exclude<TipoLeitura, 'artigo'>; cor: string; ano?: string; discreta?: boolean }) {
  const aj = useDoc('ajustes');
  const regs = useDoc('leitura')?.regs ?? [];
  const ano = anoP ?? today().slice(0, 4);
  const metas = aj?.metasLeitura ?? {};
  const meta = num(metas[ano]?.[tipo]);
  const anterior = num(metas[String(Number(ano) - 1)]?.[tipo]);
  const [editando, setEditando] = useState(false);
  const [txt, setTxt] = useState('');
  const salvar = (v: number | null) => {
    setDoc('ajustes', d => {
      const m = { ...(d.metasLeitura ?? {}) };
      const doAno = { ...(m[ano] ?? {}) };
      if (v && v > 0) doAno[tipo] = v; else delete doAno[tipo];
      m[ano] = doAno;
      return { ...d, metasLeitura: m };
    });
    setEditando(false);
  };
  const feitos = concluidosNoAno(regs, tipo, ano);
  const p = meta ? Math.min(100, Math.round((feitos / meta) * 100)) : 0;
  const { faltam, porMes } = ritmoMeta(meta, feitos, ano);
  const nome = tipo === 'livro' ? t('{a} de {n} livro(s) em {ano}', { a: feitos, n: meta, ano })
    : tipo === 'midia' ? t('{a} de {n} título(s) assistido(s) em {ano}', { a: feitos, n: meta, ano })
    : t('{a} de {n} curso(s) concluído(s) em {ano}', { a: feitos, n: meta, ano });
  const ritmo = tipo === 'livro' ? t('Faltam {f} livro(s): dá cerca de {m} por mês', { f: faltam, m: porMes })
    : tipo === 'midia' ? t('Faltam {f} título(s): dá cerca de {m} por mês', { f: faltam, m: porMes })
    : t('Faltam {f} curso(s): dá cerca de {m} por mês', { f: faltam, m: porMes });

  if (editando) return (
    <View style={{ backgroundColor: C.card, borderRadius: 14, padding: 12, gap: 8, borderWidth: 1, borderColor: alpha(cor, 0.4) }}>
      <Text style={{ color: C.text, fontWeight: '700' }}>🎯 {t('Meta de {ano}', { ano })} · {t(NOME_TIPO[tipo])}</Text>
      <Field value={txt} onChangeText={x => setTxt(x.replace(/\D/g, '').slice(0, 4))} keyboardType="number-pad" placeholder={t('Quantos no ano?')} autoFocus />
      <View style={{ flexDirection: 'row', gap: 8, flexWrap: 'wrap' }}>
        <Chip small cor={cor} on label={t('Salvar')} onPress={() => salvar(num(txt))} />
        {!!meta && <Chip small cor={C.danger} on={false} label={t('Apagar a meta')} onPress={() => salvar(null)} />}
        <Chip small cor={cor} on={false} label={t('Cancelar')} onPress={() => setEditando(false)} />
      </View>
    </View>
  );
  const editar = (v?: number) => { setTxt(v ? String(v) : ''); setEditando(true); };
  if (!meta) {
    // ano novo: sugere a meta do ano anterior, e a pessoa confirma
    if (anterior) return (
      <View style={{ backgroundColor: alpha(cor, 0.08), borderRadius: 14, padding: 10, gap: 6 }}>
        <Text style={{ color: C.text, fontSize: 13 }}>🎯 {t('Meta de {ano}: usar a de {antes} ({n})?', { ano, antes: Number(ano) - 1, n: anterior })}</Text>
        <View style={{ flexDirection: 'row', gap: 8 }}>
          <Chip small cor={cor} on label={t('Usar')} onPress={() => salvar(anterior)} />
          <Chip small cor={cor} on={false} label={t('Outra')} onPress={() => editar(anterior)} />
        </View>
      </View>
    );
    return discreta
      ? <Pressable onPress={() => editar()} hitSlop={6} style={{ alignSelf: 'flex-start' }}><Text style={{ color: C.sub, fontSize: 12 }}>🎯 {t('Definir meta do ano')} ▸</Text></Pressable>
      : <Chip small cor={cor} on={false} label={'🎯 ' + t('Definir meta de {ano}', { ano }) + ` · ${t(NOME_TIPO[tipo])}`} onPress={() => editar()} />;
  }
  return (
    <Pressable onPress={() => editar(meta)} style={{ backgroundColor: C.card, borderRadius: 14, padding: 12, gap: 6, borderWidth: 1, borderColor: alpha(cor, 0.35) }}>
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
        <Text style={{ color: C.text, fontWeight: '800', flex: 1 }}>🎯 {nome}</Text>
        <Text style={{ color: cor, fontSize: 12, fontWeight: '700' }}>{t('Mudar')}</Text>
      </View>
      <View style={{ height: 6, borderRadius: 3, backgroundColor: C.card2, overflow: 'hidden' }}>
        <View style={{ width: `${p}%`, height: 6, borderRadius: 3, backgroundColor: feitos >= meta ? C.ok : cor }} />
      </View>
      <Text style={{ color: feitos >= meta ? C.ok : C.sub, fontSize: 12 }}>{feitos >= meta ? '🎉 ' + t('Meta do ano cumprida') : `${p}% · ${ritmo}`}</Text>
    </Pressable>
  );
}

export function RelatorioLeitura({ visivel, cor, onFechar }: { visivel: boolean; cor: string; onFechar: () => void }) {
  const regs = useDoc('leitura')?.regs ?? [];
  const [modo, setModo] = useState<Modo>('mes');
  const [ini, setIni] = useState(() => inicioMes(today()));
  const [fimEsc, setFimEsc] = useState(() => today());
  const [tipo, setTipo] = useState<TipoLeitura | 'todos'>('todos');
  const [pasta, setPasta] = useState<string | null>(null);
  const [genero, setGenero] = useState<string | null>(null);
  const [refPdf, setRefPdf] = useState<Estilo | null>(null);
  const [escolher, setEscolher] = useState<'ini' | 'fim' | null>(null);
  const [gerando, setGerando] = useState(false);

  const fim = fimDe(modo, ini, fimEsc < ini ? ini : fimEsc);
  const pastas = regs.filter(r => r.col === 'pasta');
  const daPasta = pasta ? new Set((pastas.find(p => p.id === pasta)?.c.itens ?? []) as string[]) : null;
  const generos = [...new Set(regs.filter(r => (r.col === 'livro' || r.col === 'midia') && (tipo === 'todos' || r.col === tipo)).map(r => String(r.c.genero ?? '')).filter(g => g && g !== 'Outro'))].sort();
  const base = regs.filter(r => tipoDe(r) && (tipo === 'todos' || r.col === tipo) && (!daPasta || daPasta.has(r.id)) && (!genero || r.c.genero === genero));
  const linhas = itensDoPeriodo(base, ini, fim);
  const de = (tp: TipoLeitura, g: Linha['grupo']) => linhas.filter(l => l.tipo === tp && l.grupo === g);
  const mostra = (tp: TipoLeitura) => tipo === 'todos' || tipo === tp;

  const pags = porDia(base, 'livro', ini, fim), aulas = porDia(base, 'curso', ini, fim);
  const somaDias = (d: Record<string, number>) => Object.values(d).reduce((a, b) => a + b, 0);
  const finais = linhas.filter(l => l.grupo === 'final');
  const semData = linhas.filter(l => l.semData && l.grupo !== 'quero').length;
  const horas = de('curso', 'final').reduce((a, l) => a + num(l.r.c.cargaHoraria), 0);

  // gráfico: páginas (livros ou todos), aulas (cursos) ou títulos assistidos (filmes), por dia ou por mês
  const nDias = Math.round((parseYmd(fim).getTime() - parseYmd(ini).getTime()) / 86400000) + 1;
  const porMesG = modo === 'ano' || nDias > 62;
  const serie: Record<string, number> = tipo === 'curso' ? aulas : tipo === 'midia'
    ? finais.reduce<Record<string, number>>((a, l) => { a[l.data] = (a[l.data] ?? 0) + 1; return a; }, {}) : pags;
  const barras = porMesG
    ? (() => { const ms: string[] = []; for (let m = inicioMes(ini); m <= fim; m = somaMes(m, 1)) ms.push(m); return ms; })()
      .map(m => ({ v: Object.entries(serie).filter(([d]) => d.slice(0, 7) === m.slice(0, 7)).reduce((a, [, n]) => a + n, 0), rot: t(MES_CURTO[parseYmd(m).getMonth()]) }))
    : Array.from({ length: nDias }, (_, i) => { const d = addDays(ini, i); return { v: serie[d] ?? 0, rot: modo === 'semana' ? t(DIAS[i]) : i === 0 || (i + 1) % 5 === 0 ? String(parseYmd(d).getDate()) : '' }; });
  const rotGrafico = tipo === 'curso' ? (porMesG ? t('AULAS POR MÊS') : t('AULAS POR DIA')) : tipo === 'midia' ? (porMesG ? t('ASSISTIDOS POR MÊS') : t('ASSISTIDOS POR DIA')) : (porMesG ? t('PÁGINAS POR MÊS') : t('PÁGINAS POR DIA'));

  const atual = modo !== 'datas' && ini === inicioDe(modo, today());
  const nomeMesAno = (s: string) => { const d = parseYmd(s); return t('{mes} de {ano}', { mes: nomeMes(d.getMonth()), ano: d.getFullYear() }); };
  const titulo = modo === 'semana' ? (atual ? t('Esta semana') : t('Semana de {d}', { d: brYmd(ini) })) : modo === 'mes' ? (atual ? t('Este mês') : nomeMesAno(ini))
    : modo === 'ano' ? (atual ? t('Este ano') : ini.slice(0, 4)) : t('Datas escolhidas');
  const mudarModo = (m: Modo) => { if (m === modo) return; setModo(m); if (m === 'datas') { setFimEsc(fim); return; } setIni(inicioDe(m, today())); };
  const andar = (n: number) => setIni(modo === 'semana' ? addDays(ini, 7 * n) : modo === 'mes' ? somaMes(ini, n) : `${Number(ini.slice(0, 4)) + n}-01-01`);

  // frases do resumo
  const fLivros = [t('Li {a} livro(s) · Parei {b} · Estou lendo {c}', { a: de('livro', 'final').length, b: de('livro', 'parei').length, c: de('livro', 'andamento').length }),
    t('Quero ler {a} · Abandonei {b}', { a: de('livro', 'quero').length, b: de('livro', 'abandonei').length }),
    t('{p} página(s) lida(s) em {d} dia(s) · melhor sequência: {s} dia(s)', { p: somaDias(pags), d: Object.values(pags).filter(n => n > 0).length, s: melhorSequencia(pags) })];
  const porTipoMidia = Object.entries(de('midia', 'final').reduce<Record<string, number>>((a, l) => { const k = String(l.r.c.tipo || 'Outro'); a[k] = (a[k] ?? 0) + 1; return a; }, {}))
    .map(([k, n]) => `${t(k)}: ${n}`).join(' · ');
  const fMidia = [t('Assisti {a} · Estou assistindo {b} · Parei {c} · Quero assistir {d}', { a: de('midia', 'final').length, b: de('midia', 'andamento').length,
    c: de('midia', 'parei').length + de('midia', 'abandonei').length, d: de('midia', 'quero').length }), porTipoMidia];
  const fArtigos = [t('Li {a} · Lendo {b} · Parei {c} · Quero ler {d}', { a: de('artigo', 'final').length, b: de('artigo', 'andamento').length,
    c: de('artigo', 'parei').length + de('artigo', 'abandonei').length, d: de('artigo', 'quero').length })];
  const fCursos = [t('Concluí {a} · Fazendo {b} · Parei {c}', { a: de('curso', 'final').length, b: de('curso', 'andamento').length, c: de('curso', 'parei').length + de('curso', 'abandonei').length }),
    t('{a} aula(s) assistida(s) no período · {h} hora(s) de cursos concluídos', { a: somaDias(aulas), h: horas })];

  // lista item a item (sem anotações pessoais)
  const dataBr = (d: unknown) => (d ? brYmd(String(d)) : '');
  const linhaLivro = (r: Reg) => {
    const tot = num(r.c.paginas), at = Math.min(tot || Infinity, num(r.c.atual)), pc = tot ? Math.round((at / tot) * 100) : 0;
    return [String(r.c.titulo || ''), String(r.c.autor || ''), t(String(r.c.status || '')), [dataBr(r.c.inicio), dataBr(r.c.fim || r.c.parada)].filter(Boolean).join(' – '),
      tot ? t('pág. {a} de {n}', { a: at, n: tot }) : '', tot ? t('{p}% lido · falta {f}%', { p: pc, f: 100 - pc }) : '', estrelas(num(r.c.nota))];
  };
  const linhaMidia = (r: Reg) => [String(r.c.titulo || ''), t(String(r.c.tipo || '')), t(String(r.c.genero || '')), t(String(r.c.status || '')),
    dataBr(r.c.assistido || r.c.parada || r.c.inicio), num(r.c.temporada) ? `T${num(r.c.temporada)}${num(r.c.episodio) ? 'E' + num(r.c.episodio) : ''}` : num(r.c.episodio) ? `E${num(r.c.episodio)}` : '', estrelas(num(r.c.nota))];
  const linhaCurso = (r: Reg) => {
    const tot = num(r.c.aulas), f = num(r.c.feitas), pc = tot ? Math.round((Math.min(f, tot) / tot) * 100) : 0;
    return [String(r.c.nome || ''), String(r.c.plataforma || ''), t(String(r.c.status || '')), [dataBr(r.c.inicio), dataBr(r.c.fim || r.c.parada)].filter(Boolean).join(' – '),
      tot ? t('{a} de {n} aulas ({p}%)', { a: f, n: tot, p: pc }) : '', num(r.c.cargaHoraria) ? `${num(r.c.cargaHoraria)} h` : '', estrelas(num(r.c.nota))];
  };
  const doTipo = (tp: TipoLeitura) => linhas.filter(l => l.tipo === tp);
  const linhaArtigo = (r: Reg) => [String(r.c.titulo || ''), String(r.c.autor || ''), t(String(r.c.tipo || '')), String(r.c.veiculo || ''), t(String(r.c.status || '')),
    [dataBr(r.c.inicio), dataBr(r.c.fim || r.c.parada)].filter(Boolean).join(' – '), estrelas(num(r.c.nota))];
  const linhaDe = (l: Linha) => (l.tipo === 'livro' ? linhaLivro(l.r) : l.tipo === 'midia' ? linhaMidia(l.r) : l.tipo === 'artigo' ? linhaArtigo(l.r) : linhaCurso(l.r));

  const exportar = async () => {
    if (gerando) return;
    setGerando(true);
    try {
      const periodo = `${brYmd(ini)} – ${brYmd(fim)}`;
      const filtros = [tipo === 'todos' ? t('Todos') : t(NOME_TIPO[tipo]), pasta ? `📁 ${pastas.find(p => p.id === pasta)?.c.nome ?? ''}` : '', genero ? t(genero) : ''].filter(Boolean).join(' · ');
      const cab: Record<TipoLeitura, string[]> = {
        livro: [t('Título'), t('Autor'), t('Situação'), t('Datas'), t('Páginas'), '%', t('Nota')],
        midia: [t('Título'), t('Tipo'), t('Gênero'), t('Situação'), t('Data'), t('Episódio'), t('Nota')],
        curso: [t('Curso'), t('Plataforma'), t('Situação'), t('Datas'), t('Aulas'), t('Horas'), t('Nota')],
        artigo: [t('Título'), t('Autor'), t('Tipo'), t('Revista, site ou veículo'), t('Situação'), t('Datas'), t('Nota')],
      };
      const blocos: BlocoPdf[] = [
        { t: 'titulo', texto: t('Relatório de leitura e mídia') },
        { t: 'sub', texto: `${titulo} · ${periodo} · ${filtros}` },
        { t: 'linha' },
        ...(mostra('livro') ? fLivros.map(x => ({ t: 'par', texto: x } as BlocoPdf)) : []),
        ...(mostra('midia') ? fMidia.filter(Boolean).map(x => ({ t: 'par', texto: x } as BlocoPdf)) : []),
        ...(mostra('artigo') ? fArtigos.map(x => ({ t: 'par', texto: x } as BlocoPdf)) : []),
        ...(mostra('curso') ? fCursos.map(x => ({ t: 'par', texto: x } as BlocoPdf)) : []),
        ...(semData ? [{ t: 'par', texto: t('{n} item(ns) sem data de mudança de situação: usei a data de criação.', { n: semData }) } as BlocoPdf] : []),
      ];
      for (const tp of TODOS_TIPOS.filter(mostra)) {
        const ls = doTipo(tp);
        if (!ls.length) continue;
        blocos.push({ t: 'espaco', h: 6 }, { t: 'par', negrito: true, texto: t(NOME_TIPO[tp]) },
          { t: 'tabela', colunas: cab[tp], larguras: [0.24, 0.16, 0.12, 0.16, 0.12, 0.1, 0.1], linhas: ls.map(l => linhaDe(l).map(x => x.replace(/★+/g, m => `${m.length}/5`))) });   // a fonte do PDF não tem ★
      }
      if (refPdf) {
        const refs = listaDeReferencias(linhas.filter(l => l.grupo !== 'quero').map(l => l.r), refPdf);
        if (refs.length) blocos.push({ t: 'espaco', h: 8 }, { t: 'par', negrito: true, texto: `${t('Referências')} (${refPdf.toUpperCase()})` }, ...refs.map(x => ({ t: 'par', texto: x } as BlocoPdf)));
      }
      marcarAbaAtual('leitura');   // o PDF entra no histórico de relatórios da aba
      await gerarECompartilharPdf(`${t('Relatório de leitura e mídia')} ${brYmd(ini)} a ${brYmd(fim)}`, blocos, 'My Planner');
    } catch (e: any) {
      Alert.alert(t('Não foi possível gerar o PDF.'), String(e?.message ?? e));
    } finally { setGerando(false); }
  };

  const Frases = ({ l }: { l: string[] }) => <>{l.filter(Boolean).map((x, i) => <Text key={i} style={{ color: i ? C.sub : C.text, fontSize: i ? 13 : 17, fontWeight: i ? '600' : '800' }}>{x}</Text>)}</>;
  return (
    <Sheet visible={visivel} onClose={onFechar} title={t('Relatório de leitura e mídia')} larga>
      <ScrollView contentContainerStyle={{ padding: 16, gap: 14, paddingBottom: 60 }}>
        <View style={{ flexDirection: 'row', gap: 8, justifyContent: 'center', flexWrap: 'wrap' }}>
          {([['semana', N_('Semana')], ['mes', N_('Mês')], ['ano', N_('Ano')], ['datas', N_('Datas à escolha')]] as [Modo, string][]).map(([m, l]) =>
            <Chip key={m} small cor={cor} on={modo === m} label={t(l)} onPress={() => mudarModo(m)} />)}
        </View>
        <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={{ gap: 6 }}>
          <Chip small cor={cor} on={tipo === 'todos'} label={t('Todos')} onPress={() => { setTipo('todos'); setGenero(null); }} />
          {TODOS_TIPOS.map(tp => <Chip key={tp} small cor={cor} on={tipo === tp} label={t(NOME_TIPO[tp])} onPress={() => { setTipo(tp); setGenero(null); }} />)}
        </ScrollView>
        {(pastas.length > 0 || generos.length > 0) && (
          <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={{ gap: 6 }}>
            {pastas.map(p => <Chip key={p.id} small cor={String(p.c.cor || cor)} on={pasta === p.id} label={`${p.c.icone || '📁'} ${p.c.nome || t('Pasta')}`} onPress={() => setPasta(pasta === p.id ? null : p.id)} />)}
            {generos.map(g => <Chip key={g} small cor={cor} on={genero === g} label={t(g)} onPress={() => setGenero(genero === g ? null : g)} />)}
          </ScrollView>
        )}
        {modo === 'datas' ? (
          <View style={{ flexDirection: 'row', gap: 8, justifyContent: 'center' }}>
            <Chip cor={cor} on label={`${t('De')} ${brYmd(ini)}`} onPress={() => setEscolher('ini')} />
            <Chip cor={cor} on label={`${t('Até')} ${brYmd(fim)}`} onPress={() => setEscolher('fim')} />
          </View>
        ) : <NavPeriodo cor={cor} titulo={titulo} datas={`${brYmd(ini)} – ${brYmd(fim)}`} onAnterior={() => andar(-1)} onProximo={() => andar(1)} podeProximo={!atual} />}

        <CartaoTotal valor={String(finais.length)} legenda={tipo === 'livro' ? t('livros lidos no período') : tipo === 'midia' ? t('títulos assistidos no período') : tipo === 'curso' ? t('cursos concluídos no período') : tipo === 'artigo' ? t('artigos e sites lidos no período') : t('concluídos no período')} />

        {modo === 'ano' && (
          <View style={{ gap: 8 }}>
            {(['livro', 'midia', 'curso'] as const).filter(mostra).map(tp => <MetaAnual key={tp} tipo={tp} cor={cor} ano={ini.slice(0, 4)} discreta={false} />)}
          </View>
        )}

        {mostra('livro') && <View style={{ gap: 4 }}><Text style={{ color: C.sub, fontSize: 12, fontWeight: '800' }}>📚 {t('LIVROS')}</Text><Frases l={fLivros} /></View>}
        {mostra('midia') && <View style={{ gap: 4 }}><Text style={{ color: C.sub, fontSize: 12, fontWeight: '800' }}>🎬 {t('FILMES E SÉRIES')}</Text><Frases l={fMidia} /></View>}
        {mostra('artigo') && <View style={{ gap: 4 }}><Text style={{ color: C.sub, fontSize: 12, fontWeight: '800' }}>📰 {t('ARTIGOS E SITES')}</Text><Frases l={fArtigos} /></View>}
        {mostra('curso') && <View style={{ gap: 4 }}><Text style={{ color: C.sub, fontSize: 12, fontWeight: '800' }}>🧑‍💻 {t('CURSOS')}</Text><Frases l={fCursos} /></View>}
        {semData > 0 && <Text style={{ color: C.mute, fontSize: 12 }}>ℹ️ {t('{n} item(ns) sem data de mudança de situação: usei a data de criação.', { n: semData })}</Text>}

        <Text style={{ color: C.sub, fontSize: 12, fontWeight: '800' }}>{rotGrafico}</Text>
        <Barras valores={barras.map(b => b.v)} rotulos={barras.map(b => b.rot)} cor={cor} largas={modo === 'semana' || barras.length <= 12} />

        {TODOS_TIPOS.filter(mostra).map(tp => {
          const ls = doTipo(tp);
          return (
            <View key={tp} style={{ gap: 8 }}>
              <Text style={{ color: C.sub, fontSize: 12, fontWeight: '800' }}>{t('POR ITEM')} · {t(NOME_TIPO[tp]).toUpperCase()} ({ls.length})</Text>
              {!ls.length && <Text style={{ color: C.mute, fontSize: 13 }}>{t('Nada neste período.')}</Text>}
              {ls.map(l => { const [tit, ...resto] = linhaDe(l); return (
                <View key={l.r.id} style={{ backgroundColor: C.card, borderRadius: 14, padding: 12, gap: 4 }}>
                  <Text style={{ color: C.text, fontWeight: '700' }}>{tit}</Text>
                  <Text style={{ color: C.sub, fontSize: 12 }}>{resto.filter(Boolean).join(' · ')}{l.semData ? ' · ℹ️' : ''}</Text>
                </View>); })}
            </View>
          );
        })}

        <View style={{ gap: 6 }}>
          <Text style={{ color: C.sub, fontSize: 12 }}>{t('Incluir lista de referências no PDF')}</Text>
          <View style={{ flexDirection: 'row', gap: 6 }}>
            <Chip small cor={cor} on={!refPdf} label={t('Não')} onPress={() => setRefPdf(null)} />
            <Chip small cor={cor} on={refPdf === 'abnt'} label="ABNT" onPress={() => setRefPdf('abnt')} />
            <Chip small cor={cor} on={refPdf === 'apa'} label="APA" onPress={() => setRefPdf('apa')} />
          </View>
        </View>
        <Btn cor={cor} outline disabled={gerando} label={'📄 ' + (gerando ? t('Gerando…') : t('Exportar em PDF'))} onPress={exportar} />
        <Text style={{ color: C.mute, fontSize: 11 }}>{t('O PDF não leva as suas anotações, resumos e comentários.')}</Text>
        <RelatoriosGerados aba="leitura" cor={cor} />
      </ScrollView>
      {escolher && <PickerModal mode="date" cor={cor} value={parseYmd(escolher === 'ini' ? ini : fim)}
        onPick={d => { const v = ymd(d); if (escolher === 'ini') { setIni(v); if (v > fimEsc) setFimEsc(v); } else setFimEsc(v < ini ? ini : v); }} onClose={() => setEscolher(null)} />}
    </Sheet>
  );
}
